import { ApiQuery, Filter, FilterOperators } from "~/modules/filters/types";

// // // //

/**
 * Merges an optional query with a base query, combining their filters using AND logic.
 * If the base query has an AND filter with multiple filters, they are flattened into the merged result.
 */
export function mergeApiQueries(props: {
    query?: Partial<ApiQuery>;
    baseQuery: ApiQuery;
}): ApiQuery {
    const { query, baseQuery } = props;

    // Fallback to base query if no query is provided
    if (!query) {
        return baseQuery;
    }

    // Get the filter from base query (always applied if present)
    const baseFilter: Filter | undefined = baseQuery.filter as Filter;

    // Get the filter from query (optional)
    const queryFilter: Filter | undefined = query.filter as Filter;

    // If no base filter, just return props.query with fallback for baseQuery defaults
    // NOTE - we include `baseQuery` here just in case sort/pagination aren't present on `props.query`
    if (!baseFilter) {
        return {
            ...baseQuery,
            ...query,
        };
    }

    // If there is no query.filter, return `props.query` mixed in with `baseQuery.filter`
    // NOTE - we include `baseQuery` here just in case sort/pagination aren't present on `props.query`
    if (!queryFilter) {
        return {
            ...baseQuery,
            ...query,
            filter: baseQuery.filter,
        };
    }

    // If there is both baseFilter and queryFilter -> merge them
    const mergedFilters: Filter[] = [];

    // Check if baseQuery.filter.filters is an array and baseQuery.filter.operator is FilterOperators.And
    if (
        Array.isArray(baseFilter.filters) &&
        baseFilter.operator === FilterOperators.And
    ) {
        // If it is, add all the baseFilter.filters to the mergedFilters array
        baseFilter.filters.forEach((f) => mergedFilters.push(f));
    } else {
        // Otherwise, just add the baseFilter to the mergedFilters array
        mergedFilters.push(baseFilter);
    }

    // Add query.filter to the mergedFilters array
    mergedFilters.push(queryFilter);

    // Construct + return the final API query with merged filters
    const apiQuery: ApiQuery = {
        ...baseQuery,
        ...query,
        filter: {
            operator: FilterOperators.And,
            filters: mergedFilters,
        },
    };

    return apiQuery;
}
